
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CreditCard, Loader2, AlertTriangle } from 'lucide-react';
import { StripePayout } from '@/types/stripe-finances';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

interface PayoutsTableProps {
  isLoading: boolean;
  error: Error | null;
  payouts: StripePayout[] | null;
}

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const PayoutsTable: React.FC<PayoutsTableProps> = ({ isLoading, error, payouts }) => {
  const { t } = useTranslation('finances');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-xl">
          <CreditCard className="mr-2 h-5 w-5" />
          {t('payouts.title')}
        </CardTitle>
        <CardDescription>
          {t('payouts.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {error && (
          <div className="flex items-center text-red-500">
            <AlertTriangle className="mr-2 h-5 w-5" />
            <span>{t('payouts.failedToLoad')}</span>
          </div>
        )}

        {!isLoading && !error && payouts && (
          <>
            {payouts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('payouts.noPayouts')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('payouts.arrivalDate')}</TableHead>
                      <TableHead>{t('payouts.amount')}</TableHead>
                      <TableHead>{t('payouts.method')}</TableHead>
                      <TableHead>{t('payouts.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payouts.map((payout) => (
                      <TableRow key={payout.id}>
                        <TableCell>{formatDate(payout.arrival_date)}</TableCell>
                        <TableCell>{formatCurrency(payout.amount, payout.currency)}</TableCell>
                        <TableCell>{payout.method || t('payouts.standard')}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            payout.status === 'paid' 
                              ? 'bg-green-100 text-green-800' 
                              : payout.status === 'pending' 
                                ? 'bg-amber-100 text-amber-800' 
                                : payout.status === 'failed'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                          }`}>
                            {payout.status === 'paid' ? t('payouts.paid') :
                             payout.status === 'pending' ? t('payouts.pending') :
                             payout.status === 'failed' ? t('payouts.failed') :
                             payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PayoutsTable;
